import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, MapPin, Camera, Video, Megaphone, TrendingUp, Target, Users, BarChart3, Trophy, Lightbulb, 
         Layers, Share2, DollarSign, FileText, Clock, Star, Briefcase, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { useFadeIn } from '../hooks/useFadeIn';

const PlantazeOffer = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === 'plantazeunframed') {
      setIsAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPassword('');
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // Fade-in animation hooks
  const [headerRef, headerVisible] = useFadeIn({ delay: 100 });
  const [videoRef, videoVisible] = useFadeIn({ delay: 100 });
  const [whoWeAreRef, whoWeAreVisible] = useFadeIn({ delay: 100 });
  const [servicesRef, servicesVisible] = useFadeIn({ delay: 100 });
  const [clientsRef, clientsVisible] = useFadeIn({ delay: 100 });
  const [goalsRef, goalsVisible] = useFadeIn({ delay: 100 });
  const [offerRef, offerVisible] = useFadeIn({ delay: 100 });
  const [responsibilitiesRef, responsibilitiesVisible] = useFadeIn({ delay: 100 });

  // Services data (from homepage)
  const services = useMemo(() => {
    return t('services.items').map((item, index) => ({
      icon: [
        <Lightbulb className="w-12 h-12" />,
        <Users className="w-12 h-12" />,
        <BarChart3 className="w-12 h-12" />,
        <Trophy className="w-12 h-12" />
      ][index],
      title: item.title,
      description: item.description,
      detail: item.detail,
      benefits: item.benefits,
      image: [
        "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDJ8fG1hcmtldGluZ3xlbnwwfHx8fDE2NzI1NjQzNzQ&ixlib=rb-4.1.0&q=85",
        "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDF8fHBhaWQlMjBhZHN8ZW58MHx8fHwxNjcyNTY0Mzc0&ixlib=rb-4.1.0&q=85",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDF8fGZ1bm5lbHN8ZW58MHx8fHwxNjcyNTY0Mzc0&ixlib=rb-4.1.0&q=85",
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDF8fGF1dG9tYXRpb258ZW58MHx8fHwxNjcyNTY0Mzc0&ixlib=rb-4.1.0&q=85"
      ][index]
    }));
  }, [t]);

  return (
    <div className="min-h-screen bg-white relative">
      {/* Password Protection Overlay */}
      {!isAuthenticated && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Blurred Background */}
          <div className="absolute inset-0 bg-white/10 backdrop-blur-xl"></div>
          
          {/* Password Form */}
          <div className="relative bg-white rounded-2xl shadow-2xl p-12 border border-gray-200 max-w-md w-full mx-6">
            <div className="text-center mb-8">
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG-2.svg" 
                alt="Unframed" 
                className="h-12 mx-auto mb-6"
              />
              <h2 className="text-2xl font-clash font-bold text-jungle-green mb-2">
                Protected Content
              </h2>
              <p className="text-gray-600 font-geist">
                Please enter the password to access this page
              </p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(false);
                  }}
                  placeholder="Enter password"
                  className={`w-full px-6 py-4 border-2 rounded-xl font-geist text-lg focus:outline-none transition-colors duration-300 ${
                    passwordError 
                      ? 'border-red-500 bg-red-50 focus:border-red-600' 
                      : 'border-gray-300 focus:border-jungle-green'
                  }`}
                  autoFocus
                />
                {passwordError && (
                  <p className="text-red-500 text-sm mt-2 font-geist">
                    Incorrect password. Please try again.
                  </p>
                )}
              </div>
              
              <button
                type="submit"
                className="w-full bg-jungle-green hover:bg-jungle-green/90 text-white font-clash font-bold py-4 px-6 rounded-xl transition-colors duration-300 text-lg"
              >
                Access Content
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content - Blurred when not authenticated */}
      <div className={!isAuthenticated ? 'filter blur-lg pointer-events-none' : ''}>
        {/* Language Switcher */}
        <div className="fixed top-4 md:top-6 right-4 md:right-6 z-[70]">
          <LanguageSwitcher />
        </div>

      {/* Header - Navigation */}
      <header 
        className="fixed top-4 md:top-6 left-4 md:left-6 z-[60] transition-all duration-500 opacity-100 translate-y-0"
      >
        <div 
          className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 md:px-8 py-4 md:py-5 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-white/20 hover:scale-105 relative z-10"
          onClick={handleBackToHome}
        >
          <div className="flex justify-center items-center">
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG-2.svg" 
              alt="Unframed" 
              className="h-6 md:h-8 w-auto pointer-events-none"
              style={{ transition: 'none', transform: 'none' }}
            />
          </div>
        </div>
      </header>

      {/* Header Section */}
      <section className="hero-section pt-8 md:pt-12 pb-16 md:pb-20 px-6 bg-jungle-green text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-jungle-green"></div>
          <img 
            src="https://agents.beunframed.com/wp-content/uploads/2025/07/Untitled-design-30.svg"
            alt="Hero Background" 
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            loading="eager"
            decoding="async"
          />
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
          <div 
            ref={headerRef}
            className={`hero-content py-8 md:py-12 fade-in-up ${headerVisible ? 'visible' : ''}`}
          >
            <h1 className="text-5xl md:text-6xl font-clash font-bold text-white mb-4">
              {t('plantazeOffer.client')}
            </h1>
            <h2 className="text-2xl md:text-3xl font-clash font-semibold text-ogre-orange mb-4">
              {t('plantazeOffer.header')}
            </h2>
            <p className="text-lg font-geist opacity-90 max-w-4xl mx-auto">
              {t('plantazeOffer.headerSubtext')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main style={{ backgroundColor: '#d3e0e0' }}>
        
        {/* Video Section */}
        <section className="py-12 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            {/* Video Container */}
            <div 
              ref={videoRef}
              className={`mb-6 fade-in-up ${videoVisible ? 'visible' : ''}`}
            >
              {currentLanguage === 'en' ? (
                <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                  <iframe
                    src="https://customer-bgwniqrr523xvn7e.cloudflarestream.com/76c41e7e1df4a7eaec10172c2517fa1f/iframe?preload=true&poster=https%3A%2F%2Fcustomer-bgwniqrr523xvn7e.cloudflarestream.com%2F76c41e7e1df4a7eaec10172c2517fa1f%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                    loading="lazy"
                    style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen={true}
                  ></iframe>
                </div>
              ) : (
                <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                  <iframe
                    src="https://customer-bgwniqrr523xvn7e.cloudflarestream.com/bd86039aa7aac6c31e5b15f6bc5d3010/iframe?preload=true&poster=https%3A%2F%2Fcustomer-bgwniqrr523xvn7e.cloudflarestream.com%2Fbd86039aa7aac6c31e5b15f6bc5d3010%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                    loading="lazy"
                    style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen={true}
                  ></iframe>
                </div>
              )}
            </div>
            
            {/* Benjamin's Info */}
            <div 
              ref={videoRef}
              className={`mb-8 fade-in-up delay-200 ${videoVisible ? 'visible' : ''}`}
            >
              <h3 className="text-2xl md:text-3xl font-clash font-bold text-jungle-green mb-2">
                Benjamin
              </h3>
              <p className="text-lg font-geist text-gray-600">
                CEO & Co-Founder
              </p>
            </div>
          </div>
        </section>

        {/* Who We Are Section (from homepage) */}
        <section className="py-20 px-6 bg-jungle-green-light">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              
              {/* Text Content */}
              <div 
                ref={whoWeAreRef}
                className={`space-y-6 fade-in-left ${whoWeAreVisible ? 'visible' : ''}`}
              >
                <h2 className="text-3xl md:text-4xl font-clash font-bold text-jungle-green leading-tight">
                  {t('whoWeAre.title')}
                </h2>
                
                <p className="text-lg font-geist text-gray-700 leading-relaxed">
                  {t('whoWeAre.intro')}
                </p>
                
                <ul className="space-y-3">
                  {t('whoWeAre.services').map((service, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle className="w-6 h-6 text-ogre-orange flex-shrink-0" />
                      <span className="font-geist text-gray-700 text-lg">{service}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="pt-4">
                  <p className="text-lg font-geist text-gray-700 mb-2">
                    {t('whoWeAre.vision')}
                  </p>
                  <p className="text-lg font-geist text-gray-700">
                    {t('whoWeAre.goal')}
                  </p>
                </div>
              </div>

              {/* Image */}
              <div 
                ref={whoWeAreRef}
                className={`relative fade-in-right delay-200 ${whoWeAreVisible ? 'visible' : ''}`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={t('whoWeAre.image')} 
                    alt="Who We Are - Team"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="400"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do Section (4 boxes instead of slider) */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 
              ref={servicesRef}
              className={`text-3xl md:text-4xl font-clash font-bold text-jungle-green text-center mb-16 fade-in-up ${servicesVisible ? 'visible' : ''}`}
            >
              {t('plantazeOffer.whatWeDo')}
            </h2>
            
            <div 
              ref={servicesRef}
              className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 fade-in-up delay-200 ${servicesVisible ? 'visible' : ''}`}
            >
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="text-ogre-orange mb-4 flex justify-center">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-clash font-bold text-jungle-green mb-3 text-center">
                    {service.title}
                  </h3>
                  <p className="font-geist text-gray-600 text-sm text-center mb-4">
                    {service.description}
                  </p>
                  
                  {/* Benefits */}
                  <div className="space-y-2">
                    <p className="font-clash font-semibold text-jungle-green text-xs">
                      {currentLanguage === 'en' ? 'Key Benefits:' : 'Ključne Prednosti:'}
                    </p>
                    <ul className="space-y-1">
                      {service.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-ogre-orange flex-shrink-0 mt-0.5" />
                          <span className="font-geist text-gray-600 text-xs">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clients Section (from homepage) */}
        <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: '#004747' }}>
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 
                ref={clientsRef}
                className={`text-4xl md:text-5xl font-clash font-bold text-white mb-8 fade-in-up ${clientsVisible ? 'visible' : ''}`}
              >
                {t('plantazeOffer.ourClients')}
              </h2>
            </div>
            
            <div 
              ref={clientsRef}
              className={`grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-8 md:gap-12 items-center justify-items-center fade-in-up delay-200 ${clientsVisible ? 'visible' : ''}`}
            >
              {t('clients.logos').map((logo, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-center p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-all duration-300 hover:scale-105"
                >
                  <img 
                    src={logo}
                    alt={`Client logo ${index + 1}`}
                    className="max-w-full max-h-16 w-auto h-auto object-contain filter brightness-0 invert opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                    decoding="async"
                    width="120"
                    height="64"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Goals Section */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 
              ref={goalsRef}
              className={`text-3xl md:text-4xl font-clash font-bold text-jungle-green text-center mb-16 fade-in-up ${goalsVisible ? 'visible' : ''}`}
            >
              {t('plantazeOffer.mainGoals.title')}
            </h2>
            
            <div 
              ref={goalsRef}
              className={`grid md:grid-cols-3 gap-8 fade-in-up delay-200 ${goalsVisible ? 'visible' : ''}`}
            >
              {[
                { icon: <Target className="w-12 h-12" />, text: t('plantazeOffer.mainGoals.goal1') },
                { icon: <TrendingUp className="w-12 h-12" />, text: t('plantazeOffer.mainGoals.goal2') },
                { icon: <Users className="w-12 h-12" />, text: t('plantazeOffer.mainGoals.goal3') }
              ].map((goal, index) => (
                <div key={index} className="text-center p-8 bg-gradient-to-br from-jungle-green-light to-white rounded-xl shadow-lg">
                  <div className="text-ogre-orange mb-6 flex justify-center">
                    {goal.icon}
                  </div>
                  <p className="font-geist text-gray-700 leading-relaxed">
                    {goal.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Offer Section - Full Width Design */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 
              ref={offerRef}
              className={`text-3xl md:text-4xl font-clash font-bold text-jungle-green text-center mb-16 fade-in-up ${offerVisible ? 'visible' : ''}`}
            >
              {currentLanguage === 'en' ? 'Offer' : 'Ponuda'}
            </h2>
            
            <div 
              ref={offerRef}
              className={`space-y-6 fade-in-up delay-200 ${offerVisible ? 'visible' : ''}`}
            >
              
              {/* 1. Marketing Strategy Creation */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <Layers className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Marketing Strategy Creation' : 'Kreiranje marketinške strategije'}
                      </h3>
                    </div>
                    <div className="bg-jungle-green-light p-4 rounded-lg">
                      <div className="flex items-start">
                        <Target className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          <span className="font-medium text-jungle-green">
                            {currentLanguage === 'en' ? 'Goal: ' : 'Cilj: '}
                          </span>
                          {currentLanguage === 'en' 
                            ? 'presenting Plantaže as a premium brand that offers the best wines in the region (and beyond) at affordable prices.'
                            : 'predstavljanje Plantaža kao premium brenda koji nudi najbolja vina u regionu (ali i šire) uz pristupačne cijene.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Content Creation */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-6">
                      <Camera className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Content Creation' : 'Kreiranje sadržaja'}
                      </h3>
                    </div>
                    
                    <div className="space-y-6">
                      {/* Main Field Work */}
                      <div className="bg-gradient-to-r from-jungle-green-light to-blue-50 p-5 rounded-lg">
                        <div className="flex items-start mb-3">
                          <MapPin className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                          <p className="font-geist text-gray-800 font-medium leading-relaxed">
                            {currentLanguage === 'en' 
                              ? '2 monthly field trips (photos + video) – up to 3h per trip.'
                              : '2 izlaska mjesečno na teren (fotografije + video) – do 3h po izlasku.'
                            }
                          </p>
                        </div>
                        
                        {/* Sub-items */}
                        <div className="ml-8 space-y-4">
                          <div className="flex items-start">
                            <Video className="w-4 h-4 text-ogre-orange mr-3 mt-1 flex-shrink-0" />
                            <div>
                              <p className="font-geist text-gray-700 font-medium mb-2">
                                {currentLanguage === 'en' 
                                  ? 'Material for creating 4 reels monthly.'
                                  : 'Materijal za kreiranje 4 reels-a mjesečno.'
                                }
                              </p>
                              <div className="bg-white/70 p-3 rounded border-l-3 border-ogre-orange">
                                <p className="font-geist text-gray-600 text-sm leading-relaxed">
                                  {currentLanguage === 'en' 
                                    ? 'Includes all aspects needed for quality and meaningful video (script creation, filming, editing, animations, video assembly).'
                                    : 'Uključeni svi aspekti potrebni da video bude kvalitetan i smislen (kreiranje skripte, snimanje, editing, animacije, sklapanje videa).'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <Camera className="w-4 h-4 text-ogre-orange mr-3 mt-1 flex-shrink-0" />
                            <p className="font-geist text-gray-700 leading-relaxed">
                              {currentLanguage === 'en' 
                                ? 'Photographs – number not predefined; sufficient photos created according to situation.'
                                : 'Fotografije – broj se ne definiše unaprijed; biće kreiran dovoljan broj fotografija u skladu sa situacijom.'
                              }
                            </p>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="w-4 h-4 text-ogre-orange mr-3 mt-1 flex-shrink-0">
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                <path d="M12 2L2 22h20L12 2z"/>
                              </svg>
                            </div>
                            <p className="font-geist text-gray-700 leading-relaxed">
                              {currentLanguage === 'en' 
                                ? 'Drone shooting/filming.'
                                : 'Slikanje/snimanje dronom.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Coordination Note */}
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 p-5 rounded-lg">
                        <div className="flex items-start mb-2">
                          <Users className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                          <p className="font-geist text-gray-800 font-medium leading-relaxed">
                            {currentLanguage === 'en' 
                              ? 'Field trips are planned and coordinated with Plantaže team for flawless execution.'
                              : 'Izlazak na teren se realizuje isplanirano i koordinisano sa timom Plantaža, kako bi realizacija bila besprekorna.'
                            }
                          </p>
                        </div>
                        <div className="ml-8">
                          <p className="font-geist text-gray-600 text-sm leading-relaxed">
                            {currentLanguage === 'en' 
                              ? 'A photographer/videographer and one marketing team member who coordinates the process go to the field.'
                              : 'Na teren izlazi fotograf/videograf i još jedna osoba iz marketing tima koja koordinira proces.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Social Media Management */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-6">
                      <Share2 className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Social Media Management' : 'Održavanje društvenih mreža'}
                      </h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Users className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Coordination with Plantaže 13. jul internal team.'
                            : 'Koordinacija sa internim timom Plantaža 13. jul.'
                          }
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <FileText className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Content preparation for social media publishing.'
                            : 'Priprema sadržaja za objavljivanje na društvenim mrežama.'
                          }
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <Calendar className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Publishing approx. 3 posts weekly and approx. 2 stories daily.'
                            : 'Objavljivanje cca. 3 posta nedjeljno i cca. 2 storija dnevno.'
                          }
                        </p>
                      </div>
                      
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 p-4 rounded-lg">
                        <div className="flex items-start">
                          <div className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                          </div>
                          <p className="font-geist text-gray-700 text-sm font-medium leading-relaxed">
                            <span className="font-semibold">
                              {currentLanguage === 'en' ? 'Note: ' : 'Napomena: '}
                            </span>
                            {currentLanguage === 'en' 
                              ? 'We will not strictly adhere to this, but will act according to needs, timing, situation and strategy.'
                              : 'ovoga se nećemo striktno pridržavati, već ćemo u skladu sa potrebama, tajmingom, situacijom i strategijom djelovati.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Traffic Purchase */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-6">
                      <TrendingUp className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Traffic Purchase' : 'Kupovina saobraćaja'}
                      </h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Share2 className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Content purchasing on Meta platforms (Facebook + Instagram) for greater visibility.'
                            : 'Kupovina sadržaja na Meta platformama (Facebook + Instagram) u svrhu veće vidljivosti.'
                          }
                        </p>
                      </div>
                      
                      <div className="p-5 rounded-lg" style={{ backgroundColor: '#004747' }}>
                        <div className="flex items-center">
                          <DollarSign className="w-6 h-6 mr-3 flex-shrink-0 text-white" />
                          <p className="font-geist text-lg font-bold text-white">
                            {currentLanguage === 'en' 
                              ? 'Budget up to €1,000 monthly (provided by Plantaže 13. jul).'
                              : 'Budžet do 1.000 EUR mjesečno (obezbjeđuje Plantaže 13. jul).'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Monthly Reports */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-6">
                      <BarChart3 className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Monthly Reports' : 'Mjesečni izvještaji'}
                      </h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <FileText className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Report on completed activities.'
                            : 'Izvještaj o urađenim aktivnostima.'
                          }
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <Lightbulb className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'Plan and recommendations for the next period.'
                            : 'Plan i preporuke za naredni period.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Service Price */}
              <div className="rounded-xl shadow-2xl p-10 text-center" style={{ backgroundColor: '#F34B31' }}>
                <div className="flex items-center justify-center mb-6">
                  <h3 className="text-3xl font-clash font-bold text-white">
                    {currentLanguage === 'en' ? 'Service Price' : 'Cijena usluga'}
                  </h3>
                </div>
                <div className="text-6xl md:text-7xl font-clash font-bold mb-4 text-white">
                  €1,000
                </div>
                <p className="text-2xl font-geist text-white">
                  {currentLanguage === 'en' ? '+ VAT / monthly' : '+ PDV / mjesečno'}
                </p>
              </div>

              {/* Additional Services */}
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                <h3 className="text-2xl font-clash font-bold text-jungle-green mb-8 text-center flex items-center justify-center">
                  <Star className="w-8 h-8 text-ogre-orange mr-3" />
                  {currentLanguage === 'en' ? 'Additional Services (As Needed)' : 'Dodatne usluge (po potrebi)'}
                </h3>
                
                <div className="space-y-6">
                  {/* Extra Content Sessions */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h4 className="font-clash font-bold text-jungle-green mb-4 flex items-center">
                      <Camera className="w-5 h-5 text-ogre-orange mr-2" />
                      {currentLanguage === 'en' ? 'Extra Content Sessions' : 'Dodatne sesije sadržaja'}
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <DollarSign className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 text-sm">
                          {currentLanguage === 'en' 
                            ? 'Additional content creation session – €150.'
                            : 'Dodatna sesija kreiranja sadržaja – 150 EUR.'
                          }
                        </p>
                      </div>
                      
                      <div className="ml-4">
                        <p className="font-geist text-gray-700 text-sm font-medium mb-2">
                          {currentLanguage === 'en' 
                            ? 'Content creation outside Podgorica:'
                            : 'Kreiranje sadržaja van Podgorice:'
                          }
                        </p>
                        <div className="space-y-1 ml-4">
                          <p className="font-geist text-gray-600 text-sm">
                            • {currentLanguage === 'en' 
                              ? 'If within 2 field trips: travel costs + €75 daily allowance.'
                              : 'Ako spada u jedno od 2 izlaska na teren: putni troškovi + 75 EUR dnevnica.'
                            }
                          </p>
                          <p className="font-geist text-gray-600 text-sm">
                            • {currentLanguage === 'en' 
                              ? 'If additional field trip: €150 + €75 daily allowance + travel costs.'
                              : 'Ako je dodatni izlazak na teren: 150 EUR + 75 EUR dnevnica + putni troškovi.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Video Production */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h4 className="font-clash font-bold text-jungle-green mb-4 flex items-center">
                      <Video className="w-5 h-5 text-ogre-orange mr-2" />
                      {currentLanguage === 'en' ? 'Video Production' : 'Video produkcija'}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <DollarSign className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 text-sm">
                          {currentLanguage === 'en' 
                            ? 'Longer promo videos (1–3 minutes) – €150/video.'
                            : 'Produkcija dužih promo videa (1–3 minuta) – 150 EUR/video.'
                          }
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Star className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 text-sm">
                          {currentLanguage === 'en' 
                            ? 'Professional models / makeup / styling at shoots – variable price (not dependent on us).'
                            : 'Profesionalni modeli / šminka / stilizacija na snimanjima – varijabilna cijena (ne zavisi od nas).'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Advertising */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h4 className="font-clash font-bold text-jungle-green mb-4 flex items-center">
                      <TrendingUp className="w-5 h-5 text-ogre-orange mr-2" />
                      {currentLanguage === 'en' ? 'Enhanced Advertising' : 'Dodatno oglašavanje'}
                    </h4>
                    <p className="font-geist text-gray-700 text-sm mb-3">
                      {currentLanguage === 'en' 
                        ? 'Advertising for budgets over €1,000:'
                        : 'Oglašavanje u slučaju budžeta preko 1.000 EUR:'
                      }
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                      <p className="font-geist text-gray-600 text-sm">• €1,000–2,000 → {currentLanguage === 'en' ? 'additional €150' : 'dodatnih 150 EUR'}</p>
                      <p className="font-geist text-gray-600 text-sm">• €2,000–4,000 → {currentLanguage === 'en' ? 'additional €400' : 'dodatnih 400 EUR'}</p>
                      <p className="font-geist text-gray-600 text-sm">• €4,000–10,000 → {currentLanguage === 'en' ? 'additional €700' : 'dodatnih 700 EUR'}</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg mt-3">
                      <p className="font-geist text-gray-700 text-xs italic leading-relaxed">
                        <span className="font-semibold">
                          {currentLanguage === 'en' ? 'Reason: ' : 'Razlog: '}
                        </span>
                        {currentLanguage === 'en' 
                          ? 'larger budgets mean more work, but also much greater responsibility to spend funds responsibly.'
                          : 'veći budžeti znače više posla, ali i mnogo veću odgovornost da se sredstva potroše odgovorno.'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Commercial Use & Events */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h4 className="font-clash font-bold text-jungle-green mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 text-ogre-orange mr-2" />
                      {currentLanguage === 'en' ? 'Commercial Use & Events' : 'Komercijalna upotreba i događaji'}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="font-geist text-gray-700 text-sm mb-1">
                          <strong>{currentLanguage === 'en' 
                            ? 'Photos for billboards / catalogs / printed materials / promotional materials'
                            : 'Fotografije za bilborde / kataloge / štampane materijale / promotivne materijale'
                          }</strong>
                        </p>
                        <p className="font-geist text-gray-600 text-sm ml-4">
                          → {currentLanguage === 'en' 
                            ? 'commercial use charged separately. €150-300'
                            : 'posebno naplaćuje se komercijalno korišćenje. 150-300 EUR'
                          }
                        </p>
                      </div>
                      
                      <div>
                        <p className="font-geist text-gray-700 text-sm mb-1">
                          <strong>{currentLanguage === 'en' 
                            ? 'Special events'
                            : 'Specijalni događaji'
                          }</strong> {currentLanguage === 'en' 
                            ? '(fairs, festivals, corporate events)'
                            : '(sajmovi, festivali, korporativni eventi)'
                          }
                        </p>
                        <p className="font-geist text-gray-600 text-sm ml-4">
                          → {currentLanguage === 'en' 
                            ? 'not included in regular package, charged by agreement. €150-300'
                            : 'ne ulaze u redovni paket, naplaćuju se po dogovoru. 150-300 EUR'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 8. Duration Note */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-8 border-l-4" style={{ borderLeftColor: '#F34B31' }}>
                <div className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <Clock className="w-6 h-6 text-ogre-orange mr-3" />
                      <h3 className="text-2xl font-clash font-bold text-jungle-green">
                        {currentLanguage === 'en' ? 'Cooperation Duration Note' : 'Napomena o dužini saradnje'}
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        </div>
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'The first contract is signed for a maximum period of 3 months, which we consider the optimal period for cooperation evaluation.'
                            : 'Prvi ugovor se potpisuje na period od maksimalno 3 mjeseca, što smatramo optimalnim periodom za evaluaciju saradnje.'
                          }
                        </p>
                      </div>
                      <div className="flex items-start">
                        <FileText className="w-5 h-5 text-jungle-green mr-3 mt-0.5 flex-shrink-0" />
                        <p className="font-geist text-gray-700 leading-relaxed">
                          {currentLanguage === 'en' 
                            ? 'After the initial contract expires, a new contract is signed based on agreement on further business conditions.'
                            : 'Nakon isteka inicijalnog ugovora, potpisuje se novi ugovor na osnovu dogovora o daljim uslovima poslovanja.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Responsibilities Section */}
        <section className="py-20 px-6 bg-jungle-green-light">
          <div className="max-w-7xl mx-auto">
            <h2 
              ref={responsibilitiesRef}
              className={`text-3xl md:text-4xl font-clash font-bold text-jungle-green text-center mb-16 fade-in-up ${responsibilitiesVisible ? 'visible' : ''}`}
            >
              {t('plantazeOffer.clientResponsibilities.title')}
            </h2>
            
            <div 
              ref={responsibilitiesRef}
              className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 fade-in-up delay-200 ${responsibilitiesVisible ? 'visible' : ''}`}
            >
              {[
                'communication', 'materials', 'approval', 'access', 'budget', 
                'resources', 'events', 'payment', 'brandConsistency'
              ].map((key, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-lg font-clash font-bold text-jungle-green mb-4">
                    {t(`plantazeOffer.clientResponsibilities.${key}.title`)}
                  </h3>
                  <div className="space-y-2">
                    {Object.keys(t(`plantazeOffer.clientResponsibilities.${key}`))
                      .filter(subKey => subKey !== 'title')
                      .map((subKey, subIndex) => (
                        <p key={subIndex} className="font-geist text-gray-700 text-sm">
                          {t(`plantazeOffer.clientResponsibilities.${key}.${subKey}`)}
                        </p>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-clash font-bold text-jungle-green">
                {t('plantazeOffer.contact.title')}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-jungle-green-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-jungle-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="font-geist text-lg text-gray-600 mb-3">
                  {t('plantazeOffer.contact.contactLabel')}
                </p>
                <a 
                  href="mailto:benjamin@beunframed.com?subject=Plantaže Offer"
                  className="font-geist font-bold text-xl text-jungle-green hover:text-ogre-orange transition-colors duration-300 cursor-pointer block"
                >
                  benjamin@beunframed.com
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-jungle-green-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-jungle-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <p className="font-geist text-lg text-gray-600 mb-3">
                  {t('plantazeOffer.contact.callLabel')}
                </p>
                <a 
                  href="tel:+38269201209"
                  className="font-geist font-bold text-xl text-jungle-green hover:text-ogre-orange transition-colors duration-300 cursor-pointer block"
                >
                  +382 69 201 209
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* This is getting long, let me break it into parts */}
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-jungle-green text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG.svg" 
              alt="Unframed" 
              className="h-10 mx-auto mb-4"
            />
            <p className="font-geist text-lg opacity-80 font-medium">
              {t('footer.tagline')}
            </p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default PlantazeOffer;